import React from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { PickersDay, PickersDayProps } from '@mui/x-date-pickers/PickersDay';
import { Tooltip } from '@mui/material';
import { Dayjs } from 'dayjs';
import 'dayjs/locale/es';
import MonthYearHeader, { CalendarMonthContext } from './MonthYearHeader';
import {
	CalendarContainer,
	DayCell,
	StyledDateCalendar,
	TooltipContent,
	EventItem,
	EventDescription,
	EventIndicatorsContainer,
	EventDot,
	EventOverflow,
} from './styles';

export interface CalendarEvent {
	id: string | number;
	date: Date | string;
	title: string;
	description?: string;
	color?: string;
}

interface CalendarProps {
	/**
	 * Array of events to display on the calendar
	 */
	events?: CalendarEvent[];
	/**
	 * Whether the calendar is read-only
	 */
	readOnly?: boolean;
	/**
	 * Locale for the calendar (default: 'es')
	 */
	locale?: string;
	/**
	 * Maximum number of event dots to show (default: 3)
	 */
	maxEventDots?: number;
	/**
	 * Fires when the visible month changes. The date is the first local day of that month.
	 */
	onMonthChange?: (month: Date) => void;
	/**
	 * `YYYY-MM` of the month to show. Keeps the grid on that month after the parent reloads it.
	 */
	month?: string;
}

const Calendar: React.FC<CalendarProps> = ({
	events = [],
	readOnly = true,
	locale = 'es',
	maxEventDots = 3,
	onMonthChange,
	month,
}) => {
	// Get today's date
	const today = React.useMemo(() => {
		const dayjs = require('dayjs');
		require('dayjs/locale/es');
		return dayjs().locale(locale);
	}, [locale]);

	const monthAnchor = React.useMemo(() => {
		const dayjs = require('dayjs');
		require('dayjs/locale/es');
		if (!month) return dayjs().locale(locale).date(1);
		const [year, monthIndex] = month.split('-').map(Number);
		return dayjs()
			.locale(locale)
			.year(year)
			.month((monthIndex ?? 1) - 1)
			.date(1);
	}, [locale, month]);

	const shownMonthIsToday =
		!month || (monthAnchor.year() === today.year() && monthAnchor.month() === today.month());

	// Group events by date
	const getEventsForDate = (date: Dayjs): CalendarEvent[] => {
		return events.filter((event) => {
			const eventDate = new Date(event.date);
			return (
				eventDate.getDate() === date.date() &&
				eventDate.getMonth() === date.month() &&
				eventDate.getFullYear() === date.year()
			);
		});
	};

	// Custom Day component to display events
	const ServerDay = (props: PickersDayProps) => {
		const { day, outsideCurrentMonth, ...other } = props;
		const dayEvents = getEventsForDate(day as Dayjs);
		const hasEvents = dayEvents.length > 0 && !outsideCurrentMonth;
		const eventCount = dayEvents.length;
		const displayCount = Math.min(eventCount, maxEventDots);

		// Create tooltip content with event titles
		const tooltipContent = hasEvents ? (
			<TooltipContent>
				{dayEvents.map((event, index) => (
					<EventItem key={event.id} isLast={index === dayEvents.length - 1}>
						<strong>{event.title}</strong>
						{event.description && (
							<EventDescription>{event.description}</EventDescription>
						)}
					</EventItem>
				))}
			</TooltipContent>
		) : (
			''
		);

		return (
			<Tooltip title={tooltipContent} arrow placement='top'>
				<DayCell>
					<PickersDay
						{...other}
						outsideCurrentMonth={outsideCurrentMonth}
						day={day}
						disableRipple
					/>
					{hasEvents && (
						<EventIndicatorsContainer>
							{Array.from({ length: displayCount }).map((_, index) => {
								const eventColor = dayEvents[index]?.color || '#4355AB';
								return <EventDot key={index} dotColor={eventColor} />;
							})}
							{eventCount > maxEventDots && (
								<EventOverflow overflowColor='#4355AB'>+</EventOverflow>
							)}
						</EventIndicatorsContainer>
					)}
				</DayCell>
			</Tooltip>
		);
	};

	return (
		<CalendarContainer>
			<LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
				<CalendarMonthContext.Provider value={month}>
					<StyledDateCalendar
						value={shownMonthIsToday ? today : null}
						referenceDate={monthAnchor}
						readOnly={readOnly}
						views={['day']}
						openTo='day'
						fixedWeekNumber={6}
						onMonthChange={(nextMonth) => {
							const value = nextMonth as Dayjs;
							onMonthChange?.(new Date(value.year(), value.month(), 1));
						}}
						slots={{
							day: ServerDay,
							calendarHeader: MonthYearHeader,
						}}
					/>
				</CalendarMonthContext.Provider>
			</LocalizationProvider>
		</CalendarContainer>
	);
};

export default Calendar;
